import { Alert, Box, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { activeMoves, computePreviewRows } from './reallocation.utils';
import { ReviewImpactTable, type ReviewBatchGroup } from './ReviewImpactTable';
import { BATCH_LABEL_IDS, type ReassignmentRowState } from './types';
import type { BatchAllocationDetail, ReallocationBatchKey } from '../../types/response';

interface Step3ReviewProps {
  selectedBatchDetails: BatchAllocationDetail[];
  rowsByBatch: Partial<Record<ReallocationBatchKey, ReassignmentRowState[]>>;
}

export const Step3Review = ({ selectedBatchDetails, rowsByBatch }: Step3ReviewProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const groups: ReviewBatchGroup[] = selectedBatchDetails.map((batch) => ({
    batchLabel: formatMessage({ id: BATCH_LABEL_IDS[batch.batch] }),
    rows: computePreviewRows(batch.perAnnotator, rowsByBatch[batch.batch] ?? []),
  }));

  const batchesWithMoves = selectedBatchDetails
    .map((batch) => ({
      label: formatMessage({ id: BATCH_LABEL_IDS[batch.batch] }),
      moves: activeMoves(rowsByBatch[batch.batch] ?? [], batch.perAnnotator),
    }))
    .filter((b) => b.moves.length > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {formatMessage({ id: 'adminProjects.reallocation.review.heading' })}
      </Typography>

      {batchesWithMoves.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: 'adminProjects.reallocation.review.noMoves' })}
        </Typography>
      ) : (
        batchesWithMoves.map((batch) => (
          <Box key={batch.label}>
            <Typography variant="subtitle1" fontWeight={600}>
              {batch.label}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: theme.spacing(3) }}>
              {batch.moves.map((move, index) => (
                <Typography component="li" variant="body2" color="text.secondary" key={index}>
                  {formatMessage(
                    { id: 'adminProjects.reallocation.review.move' },
                    {
                      count: <strong>{move.count}</strong>,
                      fromName: move.fromName,
                      toName: move.toName,
                    }
                  )}
                </Typography>
              ))}
            </Box>
          </Box>
        ))
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: 'adminProjects.reallocation.review.impactSummary' })}
        </Typography>
        <ReviewImpactTable groups={groups} />
      </Box>

      <Alert severity="warning">
        {formatMessage({ id: 'adminProjects.reallocation.review.warning' })}
      </Alert>
    </Box>
  );
};
